import type { ViewApi } from '@fullcalendar/core';
import type FullCalendar from '@fullcalendar/react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { createContext } from 'react';
import { getInitialVisibleRange } from '../../lib';
import {
  ABSENCE_LOCK_STATUS,
  type AbsenceLockState,
  type SelectedAbsence,
  type TimelineSlot,
  type VisibleRange,
} from '../../types';

const initialLockState: AbsenceLockState = {
  status: ABSENCE_LOCK_STATUS.IDLE,
  lock: null,
  message: null,
};

export type SidebarMode = 'schedule' | 'details' | 'withdraw' | null;

interface TimelineContextValue {
  selectedAbsence: SelectedAbsence | null;
  setSelectedAbsence: Dispatch<SetStateAction<SelectedAbsence | null>>;
  selectedSlot: TimelineSlot | null;
  setSelectedSlot: Dispatch<SetStateAction<TimelineSlot | null>>;
  sidebarMode: SidebarMode;
  setSidebarMode: Dispatch<SetStateAction<SidebarMode>>;
  suppressSelectionUntil: number;
  setSuppressSelectionUntil: Dispatch<SetStateAction<number>>;
  selectedUserId: number | null;
  setSelectedUserId: Dispatch<SetStateAction<number | null>>;
  closeEditForm: () => Promise<void>;
  calendarRef: RefObject<FullCalendar | null>;
  /** The month the calendar mounts on; stable for the lifetime of the page. */
  initialCalendarDate: Date;
  view: ViewApi | null;
  setView: Dispatch<SetStateAction<ViewApi | null>>;
  visibleRange: VisibleRange;
  setVisibleRange: Dispatch<SetStateAction<VisibleRange>>;
  absenceLock: AbsenceLockState;
  setAbsenceLock: Dispatch<SetStateAction<AbsenceLockState>>;
  showLegend: boolean;
  openLegend: () => void;
  closeLegend: () => void;
  showBalanceHistory: boolean;
  openBalanceHistory: () => void;
  closeBalanceHistory: () => void;
  closeAbsenceDetails: () => Promise<void>;
}

export const TimelineContext = createContext<TimelineContextValue>({
  selectedAbsence: null,
  setSelectedAbsence: () => {},
  selectedSlot: null,
  setSelectedSlot: () => {},
  sidebarMode: null,
  setSidebarMode: () => {},
  suppressSelectionUntil: 0,
  setSuppressSelectionUntil: () => {},
  selectedUserId: null,
  setSelectedUserId: () => {},
  closeEditForm: async () => {},
  calendarRef: { current: null } as RefObject<FullCalendar | null>,
  initialCalendarDate: getInitialVisibleRange().start,
  view: null,
  setView: () => {},
  visibleRange: getInitialVisibleRange(),
  setVisibleRange: () => {},
  absenceLock: initialLockState,
  setAbsenceLock: () => {},
  showLegend: false,
  openLegend: () => {},
  closeLegend: () => {},
  showBalanceHistory: false,
  openBalanceHistory: () => {},
  closeBalanceHistory: () => {},
  closeAbsenceDetails: async () => {},
});
